// PlayerInfo.tsx - Track information display (cover, title, artist)
import { KebabMenu, MenuItem } from '@/components/common/KebabMenu';
import { Icons } from '@/styles/Icon';
import { PlayerTrack } from '@/types/track_types';
import { sanitizeText } from '@/utils/xssProtection';
import React, { useState } from 'react';
import { styled } from 'styled-components';

interface PlayerInfoProps {
  track: PlayerTrack;
  onClick?: () => void;
  statusBtn?: React.ReactNode;
  menuItems?: MenuItem[];
  maxLeftWidth?: string;
  isInPlaylist?: boolean;
}

export const PlayerInfo: React.FC<PlayerInfoProps> = ({ track, onClick, statusBtn, menuItems, maxLeftWidth, isInPlaylist = false }) => {
  const [imageError, setImageError] = useState(false);

  const coverImageUrl = track.coverImage || Icons.defaultArtistBgImage;

  const handleImageError = () => {
    setImageError(true);
  };

  return (
    <Container onClick={onClick}>
      <LeftSection $maxWidth={maxLeftWidth}>
        <CoverImage
          src={imageError ? Icons.defaultArtistBgImage : coverImageUrl}
          onError={handleImageError}
          alt={track.title}
        />
        <TrackDetails>
          <TrackTitle $isInPlaylist={isInPlaylist}>{sanitizeText(track.title)}</TrackTitle>
          <ArtistName $isInPlaylist={isInPlaylist}>{sanitizeText(track.artistName)}</ArtistName>
        </TrackDetails>
      </LeftSection>
      <RightSection>
        {/* 재생시간 // TODO : duration 응답 데이터 형식 변경 여부 확인하기, 표출형식 : "mm:ss" */}
        <Duration>{track.duration}</Duration>
        {statusBtn && statusBtn}
        {menuItems && menuItems.length > 0 && (
          <MenuWrapper>
            <KebabMenu menuItems={menuItems} />
          </MenuWrapper>
        )}
      </RightSection>
    </Container>
  );
};

// Styled Components
const Container = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: ${({ onClick }) => (onClick ? 'pointer' : 'default')};

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    flex: 0 0 auto;
  }
`;

const LeftSection = styled.div<{ $maxWidth?: string }>`
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
  width: 272px;
  ${({ $maxWidth }) => $maxWidth && `max-width: ${$maxWidth};`}
`;

const RightSection = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-shrink: 0;
  margin-left: auto;
`;

const Duration = styled.span`
  font-size: 14px;
  color: ${({ theme }) => theme.colors.fontWhite};
  flex-shrink: 0;
`;

const CoverImage = styled.img`
  width: 56px;
  height: 56px;
  border-radius: 10px;
  object-fit: cover;
  flex-shrink: 0;

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    width: 60px;
    height: 60px;
  }
`;

const TrackDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  overflow: hidden;

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    display: none;
  }
`;

const TrackTitle = styled.h3<{ $isInPlaylist: boolean }>`
  font-size: ${({ $isInPlaylist }) => ($isInPlaylist ? '16px' : '24px')};
  font-weight: 400;
  color: ${({ theme, $isInPlaylist }) => $isInPlaylist ? '#FFFFFF' : theme.colors.fontBlack};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  &:hover {
    overflow: visible;
    text-overflow: unset;
    animation: marquee 10s linear infinite;
    animation-delay: 0.5s;
  }

  @keyframes marquee {
    0% {
      transform: translateX(0);
    }
    100% {
      transform: translateX(calc(-100% - 20px));
    }
  }
`;

const ArtistName = styled.p<{ $isInPlaylist: boolean }>`
  font-size: ${({ $isInPlaylist }) => ($isInPlaylist ? '12px' : '18px')};
  font-weight: 400;
  color: ${({ theme, $isInPlaylist }) => $isInPlaylist ? theme.colors.borderGrayLight : theme.colors.fontGray[100]};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const MenuWrapper = styled.div`
  margin-left: auto;
  display: flex;
  align-items: center;
`;